import type React from 'react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface ErrorDisplayProps {
  error: string | Error | { message?: string };
  onRetry?: (() => void) | null;
  feature?: string | null;
  title?: string | null;
  showNavigation?: boolean;
}

function ErrorDisplay({
  error,
  onRetry = null,
  feature = null,
  title = null,
  showNavigation = true
}: ErrorDisplayProps) {
  const navigate = useNavigate();
  const [showAdminHelp, setShowAdminHelp] = useState(false);

  // Parse error message
  const errorMessage = typeof error === 'string' ? error : (error as { message?: string })?.message || 'An error occurred';
  const errorLower = errorMessage.toLowerCase();

  // Detect error types
  const isEdgeFunctionAuth = errorLower.includes('admin access required') ||
                              errorLower.includes('super admin access required') ||
                              errorLower.includes('not authenticated');

  const isRLSError = errorLower.includes('row-level security') ||
                     errorLower.includes('permission denied for table') ||
                     errorLower.includes('policy') ||
                     errorLower.includes('rls');

  const isPermissionError = isEdgeFunctionAuth || isRLSError ||
                            errorLower.includes('unauthorized') ||
                            errorLower.includes('not authorized') ||
                            errorLower.includes('permission');

  // Determine error code and who can fix it
  let errorCode = 'ERR';
  let adminCanFix = false;

  if (isEdgeFunctionAuth) {
    errorCode = 'AUTH';
    adminCanFix = true;
  } else if (isRLSError) {
    errorCode = 'RLS';
    adminCanFix = false;
  } else if (isPermissionError) {
    errorCode = 'PERM';
    adminCanFix = true;
  }

  // Format feature name: "service_logs" -> "Service Logs"
  const formatFeatureName = (f: string): string => {
    if (!f) return '';
    return f.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  const featureDisplay = formatFeatureName(feature || '');
  const icon = isPermissionError ? '🔒' : '⚠️';
  const displayTitle = title || (isPermissionError ? 'Access Denied' : 'Something went wrong');

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.icon}>{icon}</div>
        <h2 style={styles.title}>{displayTitle}</h2>

        <p style={styles.message}>
          {isPermissionError
            ? `Unfortunately, you don't have access to ${featureDisplay || 'this page'}.`
            : "We couldn't load this page right now."
          }
        </p>

        <p style={styles.helpText}>
          {isPermissionError
            ? 'Please contact your administrator if you believe this is incorrect.'
            : 'Please try again, or contact support if the problem continues.'
          }
        </p>

        <div style={styles.actions}>
          {onRetry && !isPermissionError && (
            <button onClick={onRetry} style={styles.retryButton}>
              Try Again
            </button>
          )}
          {showNavigation && (
            <>
              <button onClick={() => navigate(-1)} style={styles.backButton}>
                Go Back
              </button>
              <button onClick={() => navigate('/')} style={styles.homeButton}>
                Go to Home
              </button>
            </>
          )}
        </div>

        {/* Admin help + Tech code (subtle footer) */}
        <div style={styles.footer}>
          <span
            style={styles.adminHelp}
            onClick={() => setShowAdminHelp(!showAdminHelp)}
          >
            {showAdminHelp ? '▼ Admin' : '▶ Admin'}
          </span>
          <code style={styles.techCode}>
            {errorCode} | {feature || 'page'}
          </code>
        </div>

        {showAdminHelp && (
          <div style={styles.adminBox}>
            {adminCanFix ? (
              <p style={styles.adminText}>
                <strong>To fix:</strong> Users → Find user → Permissions → Enable &quot;{featureDisplay || 'this feature'}&quot;
              </p>
            ) : (
              <p style={styles.adminText}>
                <strong>Escalate to Tech Support</strong>
                {isRLSError ? ' (database access issue)' : ' (system error)'}
              </p>
            )}
          </div>
        )}

        {/* Sentinel branding footer */}
        <p style={styles.poweredBy}>
          Powered by Sentinel™ Business Management
        </p>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '60vh',
    padding: '2rem',
  },
  card: {
    background: 'white',
    borderRadius: 'var(--radius-lg)',
    padding: '3rem',
    textAlign: 'center',
    maxWidth: '420px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
  },
  icon: {
    fontSize: '3.5rem',
    marginBottom: '1rem',
  },
  title: {
    margin: '0 0 1rem 0',
    fontSize: 'var(--text-4xl)',
    fontWeight: 600,
    color: '#1f2937',
  },
  message: {
    margin: '0 0 0.5rem 0',
    fontSize: 'var(--text-xl)',
    color: '#4b5563',
    lineHeight: 1.6,
  },
  helpText: {
    margin: '0 0 1.5rem 0',
    fontSize: 'var(--text-lg)',
    color: 'var(--color-text-light)',
  },
  actions: {
    display: 'flex',
    gap: '0.75rem',
    justifyContent: 'center',
    flexWrap: 'wrap',
    marginBottom: '1.5rem',
  },
  retryButton: {
    padding: '0.625rem 1.25rem',
    fontSize: 'var(--text-base)',
    fontWeight: 500,
    background: 'var(--color-primary, #772cbb)',
    border: 'none',
    borderRadius: 'var(--radius)',
    cursor: 'pointer',
    color: 'white',
  },
  backButton: {
    padding: '0.625rem 1.25rem',
    fontSize: 'var(--text-base)',
    fontWeight: 500,
    background: 'white',
    border: '1px solid #e5e7eb',
    borderRadius: 'var(--radius)',
    cursor: 'pointer',
    color: 'var(--color-sidebar-hover)',
  },
  homeButton: {
    padding: '0.625rem 1.25rem',
    fontSize: 'var(--text-base)',
    fontWeight: 500,
    background: 'var(--color-bg)',
    border: '1px solid #e5e7eb',
    borderRadius: 'var(--radius)',
    cursor: 'pointer',
    color: 'var(--color-sidebar-hover)',
  },
  footer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '1rem',
    borderTop: '1px solid #f3f4f6',
    paddingTop: '1rem',
  },
  adminHelp: {
    fontSize: 'var(--text-xs)',
    color: 'var(--color-text-light)',
    cursor: 'pointer',
    userSelect: 'none',
  },
  techCode: {
    fontSize: 'var(--text-2xs)',
    fontFamily: 'monospace',
    color: 'var(--color-border)',
  },
  adminBox: {
    marginTop: '1rem',
    padding: '0.75rem 1rem',
    background: '#fefce8',
    border: '1px solid #fef08a',
    borderRadius: 'var(--radius-md)',
    textAlign: 'left',
  },
  adminText: {
    margin: 0,
    fontSize: 'var(--text-md)',
    color: '#854d0e',
    lineHeight: 1.5,
  },
  poweredBy: {
    margin: '1.5rem 0 0 0',
    fontSize: 'var(--text-xs)',
    color: 'var(--color-border)',
    letterSpacing: '0.02em',
  },
};

export default ErrorDisplay;
